import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  AccidentIssueWithCache,
  AccidentIssueWithCacheSearch,
  DownloadAccidentIssueWithCacheAsCsvResponse,
  QueryDownloadAccidentIssueWithCacheAsCsvArgs,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { TypedDocumentNode } from '@apollo/client';
import { getProperty, queryInput } from '@/utils';
import graphApi from '@/api';
import { DownloadAccidentIssueWithCacheAsCsvDocument } from '@/api/queries';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  stationText?: string[];
  tripServicerIataAirlineCodes?: string[];
} & { [key in keyof AccidentIssueWithCacheSearch]?: any })[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadAccidentIssueWithCacheAsCsvResponse>;
}

const accidentIssueCSVKeys: (keyof AccidentIssueWithCache)[] = [
  'accidentDatetime',
  'additionalDriverScheduled',
  'anyoneInjured',
  'canVehicleBeDriven',
  'damageToOtherVehicles',
  'didDriverCallHelp',
  'dispatcherContactedManager',
  'dispatcherFirstName',
  'dispatcherLastName',
  'driverContactedManager',
  'driverFirstName',
  'driverLastName',
  'infoOfCrewMembers',
  'infoOfInjuredParties',
  'medicalNeeded',
  'safetyFirstName',
  'safetyLastName',
  'safetyNotified',
  'samsaraCameraWorking',
  'specificLocation',
  'stationText',
  'thirdPartyInformation',
  'tripFlightNumber',
  'tripServicerIataAirlineCode',
  'typeOfMedicalAttention',
  'vehicleTrackingId',
  'wasCeoCalled',
  'wasCrewInvolved',
  'wasDriverAtFault',
  'wasThirdPartyInvolved',
];

const accidentIssueCSVColumns: Record<keyof AccidentIssueWithCache, true> = Object.fromEntries(
  accidentIssueCSVKeys.map((key) => [key, true])
) as Record<keyof AccidentIssueWithCache, true>;

// SearchAccidentIssueDocument Service
export const { downloadAccidentIssueWithCacheCsv, useDownloadAccidentIssueWithCacheCsv } = ((): {
  downloadAccidentIssueWithCacheCsv: ServiceMethod;
  useDownloadAccidentIssueWithCacheCsv: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(DownloadAccidentIssueWithCacheAsCsvDocument).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadAccidentIssueWithCacheAsCsvResponse> => {
        const query: AccidentIssueWithCacheSearch[] = payload?.map((item) => ({
          createdAt: queryInput.date([item?.startDatetime, item.endDatetime]),
          stationText: queryInput.orNull(item?.stationText),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
          anyoneInjured: queryInput(item?.anyoneInjured),
        }));
        const args: QueryDownloadAccidentIssueWithCacheAsCsvArgs = {
          input: {
            query,
            columns: accidentIssueCSVColumns,
          },
        };
        const opts = { merge: options?.merge ? 'downloadAccidentIssueWithCacheAsCSV' : undefined };
        const res = await fn(args, opts);
        return getProperty('downloadAccidentIssueWithCacheAsCSV', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    downloadAccidentIssueWithCacheCsv: fetch,
    useDownloadAccidentIssueWithCacheCsv: useService,
  };
})();
